<template>
    <card>
        <div class="d-flex align-items-center mb-4">
            <div class="flex-fill">
                <h1 class="m-0">Notifications</h1>
            </div>
            <div class="d-flex" style="gap: 10px;">

                <b-form-group>
                    <b-form-radio-group v-model="selectedView" :options="selectViewOptions" button-variant="primary"
                        buttons />
                </b-form-group>
            </div>
        </div>

        <NotificationsList :key="refreshKey" :mode="selectedView" :show-default-actions="selectedView === 'active'"
            :show-admin-action="isNotificationsAdmin" @admin-send="showSendModal" @read-all="readAll"
            @archive-all="archiveAll">
        </NotificationsList>

        <b-modal id="send-modal" title="Admin Send Notification" ok-title="Send" no-fade centered
            @ok="createAdminNotification">

            <b-form-group label="Recipients">
                <multiselect v-model="adminNotificationRecipients" :options="users.map(u => u.userName)"
                    :multiple="true" :close-on-select="false" :clear-on-select="false" />
            </b-form-group>

            <b-form-group>
                <template #label>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <span>Title</span>
                        <small :class="titleCharacterClass">{{ (adminNotification.title || '').length }}/60 characters</small>
                    </div>
                </template>
                <b-input v-model="adminNotification.title" maxlength="60" />
            </b-form-group>

            <b-form-group>
                <template #label>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <span>Body</span>
                        <small :class="bodyCharacterClass">{{ (adminNotification.body || '').length }}/250 characters</small>
                    </div>
                </template>
                <b-textarea v-model="adminNotification.body" maxlength="250" />
            </b-form-group>

            <b-form-group>
                <template #label>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <span>Target</span>
                        <small :class="targetCharacterClass">{{ (adminNotification.target || '').length }}/100 characters</small>
                    </div>
                </template>
                <b-input v-model="adminNotification.target" maxlength="100" />
            </b-form-group>

            <hr>

            <b-form-group>
                <b-checkbox switch v-model="adminNotificationKeep">
                    Keep values in fields after Send
                </b-checkbox>
            </b-form-group>
        </b-modal>

    </card>
</template>

<script lang="ts">
import NotificationsList from "@/components/Notifications/NotificationsList.vue";
import { Component, Vue } from "vue-property-decorator";
import Multiselect from 'vue-multiselect';
import { User } from "@/models/User";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: {
        NotificationsList,
        Multiselect
    },
})
export default class NotificationListPage extends Vue {

    mounted() {

    }

    private refreshKey = "";
    private selectedView = "active";
    private adminNotification: any = {};
    private adminNotificationRecipients = [];
    private adminNotificationKeep = true;
    private users: User[] = [];

    get isNotificationsAdmin(): boolean {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Notifications".toLowerCase());
    }

    get selectViewOptions() {
        return [
            { text: "Active", value: 'active' },
            { text: "Archive", value: 'inactive' }
        ];
    }

    getCharacterCountClass(currentLength: number, maxLength: number): string {
        if (currentLength >= maxLength) {
            return 'text-danger'; // Red at 100%
        } else if (currentLength >= maxLength * 0.8) {
            return 'text-warning'; // Yellow at 80%
        }
        return 'text-muted'; // Default gray
    }

    get titleCharacterClass() {
        return this.getCharacterCountClass((this.adminNotification.title || '').length, 60);
    }

    get bodyCharacterClass() {
        return this.getCharacterCountClass((this.adminNotification.body || '').length, 250);
    }

    get targetCharacterClass() {
        return this.getCharacterCountClass((this.adminNotification.target || '').length, 100);
    }

    async readAll() {
        try {
            await this.$http.post("/notification/mark-all-read");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.refreshKey = new Date().toString();
        }
    }

    async archiveAll() {
        try {
            const result = await this.$bvModal.msgBoxConfirm("Are you sure you want to archive all of your notifications?", {
                title: 'Archive All Notifications?',
                okVariant: 'warning',
                okTitle: 'Archive All',
                cancelTitle: 'Cancel',
                hideHeaderClose: true,
                centered: true,
                noFade: true
            });

            if (!result) return;

            await this.$http.post("/notification/archive-all");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.refreshKey = new Date().toString();
        }
    }

    async createAdminNotification(e?: any) {

        e?.preventDefault();

        try {
            await this.$http.post("/notification/create", {
                notification: {
                    icon: "bell",
                    target: this.adminNotification.target,
                    title: this.adminNotification.title,
                    body: this.adminNotification.body,
                    category: "Admin",
                    completed: false,
                    phi: false
                },
                recipients: this.adminNotificationRecipients
            });

            this.$notification(NotificationOptions.success("Notification was scheduled"));

            if (!this.adminNotificationKeep)
                this.resetAdminNotifiation();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

    }

    resetAdminNotifiation() {
        this.adminNotification = {
            target: "/reports/notifications"
        };
        this.adminNotificationRecipients = [];
    }

    showSendModal() {
        this.resetAdminNotifiation();

        if (!this.users.length) this.loadUsers();

        this.$bvModal.show("send-modal");
    }

    async loadUsers() {
        try {
            const resp = await this.$http.get<User[]>("/user");
            this.users = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

}

</script>